import { useState } from "react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle2, Building2 } from "lucide-react";

export default function ServiceRequest() {
  const [form, setForm] = useState({
    organization_name: "", contact_name: "", contact_email: "", contact_phone: "",
    org_type: "nonprofit", requested_date: "", requested_time: "",
    potential_patients: "", location: "", notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/service-requests", {
        ...form,
        potential_patients: parseInt(form.potential_patients || "0", 10),
      });
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="App">
        <Navbar />
        <section className="max-w-2xl mx-auto px-6 pt-40 pb-32 text-center">
          <CheckCircle2 className="h-16 w-16 text-[#3E7B5A] mx-auto" />
          <h1 className="font-serif text-4xl text-[#2D2336] mt-6">Request Received</h1>
          <p className="text-lg text-[#5C5166] mt-4">
            Thank you for reaching out. Our team will review your community service
            request and contact you at {form.contact_email}.
          </p>
          <a href="/" className="inline-block mt-10 text-[#6B4C8A] font-medium hover:underline">← Back to Home</a>
        </section>
        <Footer />
      </div>
    );
  }

  return (
    <div className="App">
      <Navbar />
      <section className="max-w-3xl mx-auto px-6 pt-32 pb-24">
        <Building2 className="h-10 w-10 text-[#6B4C8A]" />
        <p className="label text-sm uppercase tracking-[0.15em] text-[#78698A] font-semibold mt-4">For Organizations</p>
        <h1 className="font-serif text-5xl text-[#2D2336] mt-3">Request Mobile Dental Service</h1>
        <p className="text-lg text-[#5C5166] mt-4">
          Community organizations, non-profits, and businesses can request our mobile
          dental services for their group. Tell us the details below.
        </p>

        <form onSubmit={submit} className="mt-10 space-y-6" data-testid="service-request-form">
          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <Label className="text-[#2D2336]">Organization Name *</Label>
              <Input required data-testid="sr-org-name" value={form.organization_name} onChange={(e) => set("organization_name", e.target.value)} className="mt-2" />
            </div>
            <div>
              <Label className="text-[#2D2336]">Organization Type *</Label>
              <Select value={form.org_type} onValueChange={(v) => set("org_type", v)}>
                <SelectTrigger className="mt-2" data-testid="sr-org-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="community">Community Organization</SelectItem>
                  <SelectItem value="nonprofit">Non-Profit</SelectItem>
                  <SelectItem value="business">Business</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[#2D2336]">Contact Name *</Label>
              <Input required data-testid="sr-contact-name" value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} className="mt-2" />
            </div>
            <div>
              <Label className="text-[#2D2336]">Contact Email *</Label>
              <Input required type="email" data-testid="sr-contact-email" value={form.contact_email} onChange={(e) => set("contact_email", e.target.value)} className="mt-2" />
            </div>
            <div>
              <Label className="text-[#2D2336]">Contact Phone</Label>
              <Input data-testid="sr-contact-phone" value={form.contact_phone} onChange={(e) => set("contact_phone", e.target.value)} className="mt-2" />
            </div>
            <div>
              <Label className="text-[#2D2336]">Potential Patients *</Label>
              <Input required type="number" min="1" data-testid="sr-patients" value={form.potential_patients} onChange={(e) => set("potential_patients", e.target.value)} className="mt-2" />
            </div>
            <div>
              <Label className="text-[#2D2336]">Requested Date *</Label>
              <Input required type="date" data-testid="sr-date" value={form.requested_date} onChange={(e) => set("requested_date", e.target.value)} className="mt-2" />
            </div>
            <div>
              <Label className="text-[#2D2336]">Requested Time *</Label>
              <Input required type="time" data-testid="sr-time" value={form.requested_time} onChange={(e) => set("requested_time", e.target.value)} className="mt-2" />
            </div>
          </div>
          <div>
            <Label className="text-[#2D2336]">Location *</Label>
            <Input required data-testid="sr-location" value={form.location} onChange={(e) => set("location", e.target.value)} className="mt-2" placeholder="Full address of the event / site" />
          </div>
          <div>
            <Label className="text-[#2D2336]">Additional Notes</Label>
            <Textarea rows={3} data-testid="sr-notes" value={form.notes} onChange={(e) => set("notes", e.target.value)} className="mt-2" />
          </div>
          <button type="submit" disabled={submitting} data-testid="sr-submit"
            className="w-full bg-[#6B4C8A] text-white py-4 rounded-full font-medium hover:bg-[#543C6E] transition-all hover:-translate-y-0.5 shadow-sm disabled:opacity-60">
            {submitting ? "Submitting..." : "Submit Request"}
          </button>
        </form>
      </section>
      <Footer />
    </div>
  );
}
